import { isHighSpecSender } from '../../../features/base/environment/videoCapability';
import {
	setFailedJitsiConnection,
	setSuccessJitsiConnection,
	setWillJitsiConnection,
} from '../../../features/base/jisti';
import JitsiMeetJS, { browser, JitsiConferenceEvents } from '../../../features/base/lib-jitsi-meet';
import { createLocalTrack } from '../../../features/base/lib-jitsi-meet/functions.any';
import { MEDIA_TYPE } from '../../../features/base/media';
import { ROLE, getBridgeSession, getLocalIDMember, getLocalMember } from '../../../features/base/members';
import { getPropertyValue } from '../../../features/base/settings';
import {
	addLocalTrack,
	destroyLocalTracks,
	getTracksByTrackIdAndMediaType,
	replaceLocalTrack,
	trackAdded,
	trackRemoved,
} from '../../../features/base/tracks';
import { getRoomOption } from '../../../features/room';
import { getLocalSharingStatus, getScreenShareTrackId } from '../../../features/screen-share';
import { createTaskQueue } from '../../util/helpers';

const _replaceLocalVideoTrackQueue = createTaskQueue();
const _replaceLocalAudioTrackQueue = createTaskQueue();
class JitsiConference {
	constructor() {
		this.conference = null;
		this.jingleSession = null;
		this.lastConstrains = null;
		this.dualRoom = null;
		this._applyEncodingsTimer = null;

		this.removeTrack = this.removeTrack.bind(this);
	}

	/**
	 * 호스트 시뮬캐스트 계층을 480/720/1080으로 재설정.
	 * lib-jitsi-meet은 배율 4/2/1(1080 캡처 시 270/540/1080)로 하드코딩되어 있어,
	 * 저사양 수신자가 720 계층을 받을 수 있도록 setParameters로 덮어쓴다.
	 * 라이브러리가 트랙 교체·재협상 시 원래 값으로 되돌릴 수 있으므로 해당 이벤트마다 재적용한다.
	 */
	applySenderEncodings(retries = 5) {
		clearTimeout(this._applyEncodingsTimer);

		// 1080 캡처(호스트+송신 고사양)일 때만 의미가 있다
		const local = getLocalMember(APP.store.getState);
		if (local?.role !== ROLE.HOST || !isHighSpecSender()) {
			console.info('[videoQuality] 시뮬캐스트 오버라이드 생략:', {
				role: local?.role,
				highSpecSender: isHighSpecSender(),
			});
			return;
		}

		const pc = this.conference?.getActiveMediaSession?.()?.peerconnection?.peerconnection;
		const videoSender = pc?.getSenders?.().find(s => s.track && s.track.kind === 'video');
		const params = videoSender?.getParameters();

		if (!params?.encodings || params.encodings.length < 3) {
			if (retries > 0) {
				this._applyEncodingsTimer = setTimeout(() => this.applySenderEncodings(retries - 1), 500);
			} else {
				console.warn('[videoQuality] video sender/encodings 획득 실패 (재시도 소진):', {
					hasSender: !!videoSender,
					encodings: params?.encodings?.length ?? 0,
				});
			}
			return;
		}

		// 배율 2.25/1.5/1 고정: 1080 캡처면 480/720/1080, 720 캡처면 320/480/720.
		// 각 계층이 아래 계층의 1.5배로 간격이 균일해 BWE 단계 강등의 낙차가 일정하다.
		const captureHeight = videoSender.track.getSettings?.()?.height;

		// 낮은 계층부터 나열. Firefox는 높은 계층부터 나열된다.
		const factors = browser.isFirefox() ? [1, 1.5, 2.25] : [2.25, 1.5, 1];
		let changed = false;

		params.encodings.forEach((encoding, i) => {
			if (encoding.scaleResolutionDownBy !== factors[i]) {
				encoding.scaleResolutionDownBy = factors[i];
				changed = true;
			}

			// 계층 상한 비트레이트를 '계층의 실제 높이' 기준으로 재조정한다.
			// joinConfig 의 maxBitratesVideo(1M/2.5M/4M)는 1080 캡처 계층(540/720/1080)
			// 기준이라, 720p 카메라(C270 등)로 캡처가 720이면 같은 값이 360/480/720 에
			// 붙어 모든 계층이 해상도 대비 과가격이 된다. 인코더는 max 까지 실제로 채워
			// 보내므로 수신 BWE가 계층 유지 비용을 감당 못해 시간이 지나며 720→480→360
			// 으로 계단식 강등이 발생했다(실측). 합산 업링크도 7.5M→약 3.9M 로 내려간다.
			if (captureHeight) {
				const maxBitrate = this._bitrateForLayerHeight(captureHeight / factors[i]);

				if (encoding.maxBitrate !== maxBitrate) {
					encoding.maxBitrate = maxBitrate;
					changed = true;
				}
			}
		});

		if (changed) {
			videoSender
				.setParameters(params)
				.then(() =>
					console.info(
						'[videoQuality] 시뮬캐스트 배율/비트레이트 적용:',
						factors,
						params.encodings.map(e => e.maxBitrate),
						`(캡처 ${captureHeight}p)`,
					),
				)
				.catch(err => console.error('[videoQuality] 시뮬캐스트 계층 적용 실패:', err));
		}
	}

	/**
	 * 계층의 실제 높이에 맞는 상한 비트레이트.
	 * 1080 캡처(480/720/1080)면 1M/2.5M/4M,
	 * 720 캡처(320/480/720)면 400k/1M/2.5M 로 계단 간격(약 2.5배)을 유지한다.
	 */
	_bitrateForLayerHeight(height) {
		if (height >= 1080) return 4000000;
		if (height >= 720) return 2500000;
		if (height >= 480) return 1000000;
		if (height >= 320) return 400000;
		return 200000;
	}

	setReceiverConstraints(data) {
		this.lastConstrains = data;

		if (this.conference && this.jingleSession && this.lastConstrains) {
			this.conference.setReceiverConstraints(data);
		}
	}

	async removeTrack(track) {
		try {
		const conferenceTracks = this.conference.getLocalTracks();
		
		if (conferenceTracks.find(track => track.deviceId === track.deviceId)) {
			await this.conference.removeTrack(track);
		} else {
			console.warn('Track is not part of the conference:', track);
		}
	} catch (err) {
		console.error('Failed to remove track:', err);
	}
	}

	// 현재 사용자가 청취자(비-HOST & !individualLive)인지. 청취자는 듣는 동안 마이크를 보유하지 않는다.
	_isListener() {
		const local = getLocalMember(APP.store.getState);
		const individualLive = getPropertyValue(APP.store.getState, 'individualLive');

		return !(local?.role === ROLE.HOST || individualLive);
	}

	// 서버 bridge 의 audio_track_id/audio_muted 를 갱신 전송한다.
	// StateListener(tracks/reducer.ts)와 동일하게 현재 bridge 를 펼치고 오디오 필드만 덮어써
	// video_track_id 등 다른 값이 유실되지 않게 한다. (서버가 jitsi 로 자동 감지하든/클라 값에 의존하든 커버)
	_updateBridgeAudioTrackId(audioTrackId, audioMuted) {
		try {
			const state = APP.store.getState();
			const bridge = getBridgeSession(state, getLocalIDMember(state));

			if (!bridge) return;

			// ★소유권 가드(핑퐁 폭풍 수정): 같은 계정 다른 탭 세션이 내 멤버 엔트리를 덮은
			// 상태면 그 엔트리(상대 세션 id·트랙 id)를 스프레드해 되쏘지 않는다 — 오염 전파 방지.
			// (중복 접속 미지원 정책. 내 세션 id 는 항상 명시적으로 스탬프한다)
			const myId = this.conference?.myUserId?.();
			if (!myId || (bridge.id && bridge.id !== myId)) return;

			APP.management.setBridgeId({
				...bridge,
				id: myId,
				audio_track_id: audioTrackId || '',
				audio_muted: audioMuted,
			});
		} catch (e) {
			console.error('bridge audio_track_id 갱신 실패:', e);
		}
	}

	// 청취자 발언 허가: 마이크 즉석 획득 → 회의 송출(join HOST 경로와 동일 순서) → 서버 bridge 갱신.
	async acquireAndPublishAudio() {
		const conference = this.conference;

		if (!conference) return;

		let newTrack = null;
		try {
			const { userSelectedMicDeviceId } = APP.store.getState()['features/base/settings'];
			newTrack = await createLocalTrack(MEDIA_TYPE.AUDIO, userSelectedMicDeviceId, 5000);

			if (!newTrack) return;

			try { newTrack.unmute && (await newTrack.unmute()); } catch (e) { /* 무시 */ }

			await conference.addTrack(newTrack);
			await APP.store.dispatch(addLocalTrack(newTrack));

			this._updateBridgeAudioTrackId(newTrack.getId ? newTrack.getId() : '', false);
		} catch (err) {
			console.error('발언용 오디오 트랙 획득 실패:', err);
			// ★송출 실패 시 획득한 마이크를 반납한다 — 방치하면 회의에 안 실린 채
			// 마이크 점유(탭 마이크 표시등 유지, 이중 캡처/BT SCO 진입 위험)가 지속된다.
			if (newTrack) {
				try { await newTrack.dispose(); } catch (e) { /* 무시 */ }
			}
		}
	}

	// 청취자 발언 회수: 회의에서 제거 + 트랙 파기(마이크 반납) → 서버 bridge 초기화.
	// (다시 듣기 상태로 돌아가므로 통신모드/BT SCO 재진입을 막기 위해 디바이스를 반납한다)
	async releaseAudio(track) {
		try {
			const jitsiTrack = track && track.track;

			if (jitsiTrack) {
				try { await this.removeTrack(jitsiTrack); } catch (e) { /* 무시 */ }
				try { await APP.store.dispatch(destroyLocalTracks([jitsiTrack])); } catch (e) { /* 무시 */ }
			}

			this._updateBridgeAudioTrackId('', true);
		} catch (err) {
			console.error('발언용 오디오 트랙 반납 실패:', err);
		}
	}


	join(connection, config, handler, isTemp, user_id) {
		// 회의실 uuid
		const meeting_uuid = getRoomOption(APP.store.getState).code;
		if (!meeting_uuid) return Promise.reject('no meeting');
		// 회의실 세팅
		const conference = connection.initJitsiConference(meeting_uuid, config);

		// 브리지 채널 오픈 직후 lib이 내보내는 초기 ReceiverVideoConstraints를 무해화한다.
		// lib 기본값(_maxFrameHeight=2160)이나 디바이스 상한(1080)을 넣으면 첫 메시지가
		// defaultConstraints:{maxHeight:N}(전 소스 구독)으로 나가 입장 즉시 모든 송신자에게
		// 키프레임을 유발한다(기존 시청자 화면 펄스, 브리지 채널 덤프로 실측). 0이면 아무것도
		// 구독하지 않고, 직후 allocateUsersByVideoQuality의 본 메시지(스테이지 소스만)가
		// 구독을 시작한다. 이후 재전송 캐시는 본 메시지로 대체되므로 이 값은 다시 쓰이지 않는다.
		conference.receiveVideoController._receiverVideoConstraints.updateReceiveResolution(0);

		const that = this;
		this.conference = conference;
		return new Promise(async (resolve, reject) => {
			// connection 실패가 발생하는 경우
			connection.addEventListener(JitsiMeetJS.events.connection.CONNECTION_FAILED, _connectionFailedHandler);

			// 회의실 입장 성공
			function _handleConferenceJoined() {
				_unsubscribe();
				// 성공
				APP.store.dispatch(setSuccessJitsiConnection(conference));

				// 회의실 이벤트 등록
				function _conferenceEventListener() {
					// 첫 참가자 입장 시 시뮬캐스트 계층이 실제 활성화되므로 재적용
					conference.on(JitsiMeetJS.events.conference.USER_JOINED, () => {
						that.applySenderEncodings();
					});

					// track 추가
					conference.on(JitsiMeetJS.events.conference.TRACK_ADDED, track => {
						if (!track) return;

						if (track.isLocal()) {
							// 종전 코드는 정의되지 않은 `err` 를 참조해 ReferenceError 로 죽었고,
							// 그래서 강사 카메라 정지(LOCAL_TRACK_STOPPED) 로그가 서버에 한 번도 남지 않았다.
							track.on(JitsiMeetJS.events.track.LOCAL_TRACK_STOPPED, async () => {
								console.warn('[track] 로컬 트랙 정지:', track.getType?.());
								await fetchErrorData({
									message: 'track stop',
									data: { type: track.getType?.(), videoType: track.videoType, deviceId: track.deviceId },
								});
							});
							return;
						}
						// if (isTemp && track.getType() === 'audio') track.mute();
						// track.muted();

						const screenTrackId = getScreenShareTrackId(APP.store.getState);
						if (getLocalSharingStatus(APP.store.getState) && track.ownerEndpointId === screenTrackId)
							return;

						APP.store.dispatch(trackAdded(track));
					});

					// track 삭제
					conference.on(JitsiMeetJS.events.conference.TRACK_REMOVED, track => {
						if (!track || track.isLocal()) {
							return;
						}

						APP.store.dispatch(trackRemoved(track));
					});

					conference.room.addListener('xmpp.session_accept', (jingleSession, ctx) => {
						that.jingleSession = jingleSession;
						that.setReceiverConstraints(that.lastConstrains);
						that.applySenderEncodings();
					});
				}

				_conferenceEventListener();
			}

			// 회의실 입장 실패
			function _handleConferenceFailed(err) {
				_unsubscribe();
				!isTemp && APP.store.dispatch(setFailedJitsiConnection(conference));
				this.conference = null;

				reject(err);
			}

			function _unsubscribe() {
				conference.off(JitsiMeetJS.events.conference.CONFERENCE_JOINED, _handleConferenceJoined);
				conference.off(JitsiMeetJS.events.conference.CONFERENCE_FAILED, _handleConferenceFailed);
			}

			// 이벤트 등록
			conference.on(JitsiMeetJS.events.conference.CONFERENCE_JOINED, _handleConferenceJoined);
			conference.on(JitsiMeetJS.events.conference.CONFERENCE_FAILED, _handleConferenceFailed);

			await conference.join();
			this._conferenceWillJoin(handler, isTemp, user_id)
				.then(() => {
					resolve(conference);
				})
				.catch(err => {
					resolve(conference);
				});
		});
	}

	leave() {
		this.conference && this.conference.leave();

		this.conference = null;
	}

	async _conferenceWillJoin(handler, isTemp, user_id) {
		const conference = this.conference;

		if (isTemp) return Promise.resolve();

		try {
			APP.store.dispatch(setWillJitsiConnection(conference));
			const { audio: hasAudioPermission, video: hasVideoPermission } =
				APP.store.getState()['features/base/devices'].permissions;

			const { userSelectedMicDeviceId, userSelectedCameraDeviceId } =
				APP.store.getState()['features/base/settings'];

			const local = getLocalMember(APP.store.getState);
			const individualLive = getPropertyValue(APP.store.getState, 'individualLive');
			// 청취자(비-HOST & !individualLive)는 듣는 동안 마이크를 보유하지 않는다 → Android/iOS 가 통화모드
			// (MODE_IN_COMMUNICATION / AVAudioSession playAndRecord)로 안 들어가 BT(버즈/에어팟) 미디어(A2DP)
			// 라우팅이 정상. 발언 허가 시 conferenceAddedTrack 에서 즉석 획득한다.
			// ※실증 테스트: 발언 시 서버가 세션중 audio_track_id 갱신을 받아 강사 라우팅하는지 확인용(안 되면 되돌림).
			const isListener = !(local?.role === ROLE.HOST || individualLive);

			// const [videoTracks, audioTracks] = await Promise.all([
			const videoTracks = hasVideoPermission
				? await handler.handlerCreateTracks({
						devices: ['video'],
						cameraDeviceId: userSelectedCameraDeviceId,
					})
				: undefined;
			// 청취자는 오디오 트랙을 생성하지 않는다(마이크 미보유 → 통화모드 회피). 발언 허가 시점에 즉석 획득.
			const audioTracks = hasAudioPermission && !isListener
				? await handler.handlerCreateTracks({
						devices: ['audio'],
						micDeviceId: userSelectedMicDeviceId,
					})
				: undefined;
			// ]);



			const localTracks = [videoTracks, audioTracks];

			for (const track of localTracks) {
				if (!track) continue;

				if (track.type === 'video') await this.setVideoMuteStatus(track);
				else if (track.type === 'audio') {
					if (local?.role === ROLE.HOST || individualLive) {
						await this.setAudioMuteStatus(track);
					} else {
						await track.mute();
					}
				}

				if (local.role !== ROLE.HOST && !individualLive) {
					await APP.store.dispatch(addLocalTrack(track));
				} else {
					try {
						await conference.addTrack(track);
						await APP.store.dispatch(addLocalTrack(track));
					} catch (err) {
						console.log('Track creation error', err);
					}
				}
			}

			let bridge = {
				id: conference.myUserId(),
				type: 'video',
				video_track_id: localTracks[0]?.getId(),
				// 청취자는 오디오 트랙이 없으므로 빈 값/음소거로 등록. 발언 허가 시 갱신 전송된다.
				audio_track_id: localTracks[1]?.getId() ?? '',
				video_muted: localTracks[0]?.isMuted(),
				audio_muted: localTracks[1] ? localTracks[1].isMuted() : true,
			};
			APP.management.setBridgeId(bridge);
			return Promise.resolve();

			// if (!individualLive) {
			// 	if (local.role === ROLE.PARTICIPANT) {
			// 		// 개인 방 - 듀얼 모니터
			// 		const dualRoom = new JitsiDual();

			// 		dualRoom.connect().then(connection => {
			// 			dualRoom.join(connection, user_id, true, userSelectedCameraDeviceId);

			// 			this.dualRoom = dualRoom;
			// 			return Promise.resolve();
			// 		});
			// 	}
			// } else {
			// 	return Promise.resolve();
			// }
		} catch (err) {
			console.log(err);
			return Promise.reject();
		}
	}

	replaceTrack(oldTrack, newTrack) {
		try {
			const result = this.conference.replaceTrack(oldTrack, newTrack);

			// 트랙 교체 시 라이브러리가 인코딩 배율을 초기화하므로 재적용
			Promise.resolve(result)
				.then(() => this.applySenderEncodings())
				.catch(() => {});

			return result;
		} catch (err) {
			APP.UI.alertMessage('트랙 교체에 실패하였습니다.');
			return false;
		}
	}

	useVideoStream(newTrack) {
		const state = APP.store.getState();

		return new Promise((resolve, reject) => {
			_replaceLocalVideoTrackQueue.enqueue(onFinish => {
				const oldTrack = getTracksByTrackIdAndMediaType(state, 'local', MEDIA_TYPE.VIDEO)?.track;

				const trackAction = oldTrack ? replaceLocalTrack(oldTrack, newTrack) : addLocalTrack(newTrack);

				APP.store
					.dispatch(trackAction)
					.then(() => {
						this.setVideoMuteStatus();
						this.applySenderEncodings();
						resolve();
					})
					.catch(err => {
						console.error('비디오 트랙 교체 실패:', err);
						APP.UI.alertMessage('비디오 트랙 교체에 실패하였습니다.', undefined, false, {
							option: 'error',
						});
					})
					.finally(() => {
						onFinish();
					});
			});
		});
	}

	useAudioStream(newTrack) {
		const state = APP.store.getState();

		return new Promise((resolve, reject) => {
			_replaceLocalAudioTrackQueue.enqueue(onFinish => {
				const oldTrack = getTracksByTrackIdAndMediaType(state, 'local', MEDIA_TYPE.AUDIO)?.track;

				// if (oldTrack === newTrack) {
				// 	resolve();
				// 	onFinish();

				// 	return;
				// }

				// Add the track to the conference if there is no existing track, replace it otherwise.
				const trackAction = oldTrack ? replaceLocalTrack(oldTrack, newTrack) : addLocalTrack(newTrack);

				APP.store
					.dispatch(trackAction)
					.then(() => {
						this.setAudioMuteStatus();
					})
					.then(resolve)
					.catch(error => {
						console.error(`useVideoStream failed: ${error}`);
						onFinish();
					})
					.then(() => {
						onFinish();
					});
			});
		});
	}

	async setVideoMuteStatus(track) {
		if (track) {
			const { userSelectedCameraMuted, request_camera } = APP.store.getState()['features/base/settings'];

			if (userSelectedCameraMuted || !request_camera) {
				await track.mute();
			}
		}
	}

	async setAudioMuteStatus(track) {
		if (track) {
			const { userSelectedAudioMuted, request_mic } = APP.store.getState()['features/base/settings'];
			if (userSelectedAudioMuted || !request_mic) await track.mute();
		}
	}
}

function _connectionFailedHandler() {
	console.log(' 회의실 연결 실패 ');
}

export default JitsiConference;
